import React from "react";
import * as teamsJs from "@microsoft/teams-js";
import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { I18nextProvider } from "react-i18next";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";

import "./styles/pages/App.css";
import TeamRequestForm from "./TeamRequestForm";
import Navigation, { NavigationTab } from "./Navigation";
import SetupPage from "./SetupPage";
import AdminPage from "./AdminPage";
import { DebugPage as DebugApprovalsPage } from "./Debug";
import RequestsPage from "./RequestsPage";
import { apiService, getTeamsUserToken } from "./services/api.service";
import { telemetryService } from "./services/telemetry.service";
import { useTeamsLocale } from "./hooks/useTeamsLocale";
import type { TeamRequest, ApiResponse, TeamTemplate, AppSettings, Administrator, TeamPrivacyType, GraphUser } from "./types";
import { buildApiUrlWithParams } from "./utils/api-url";

interface ProvisioningStatus {
  siteUrl: string | null;
  listsExist: {
    TeamRequests: boolean;
    TeamTemplates: boolean;
    AppSettings: boolean;
  };
  isConfigured: boolean;
  errors: string[];
}

// ✨ MULTI-TENANT: Interface pour l'état du tenant
interface TenantStatus {
  checking: boolean;
  exists: boolean;
  tenantId?: string;
  error?: string;
  azureTenantId?: string;
}

export default function App() {
  const [content, setContent] = React.useState("");
  const [userName, setUserName] = React.useState("");
  const [userId, setUserId] = React.useState("");
  const [userEmail, setUserEmail] = React.useState("");
  const [teamId, setTeamId] = React.useState<string | null>(null);
  const [isInTeams, setIsInTeams] = React.useState(false);
  const [debugInfo, setDebugInfo] = React.useState<string>("");
  const [currentTab, setCurrentTab] = React.useState<NavigationTab>("home");
  const [provisioningStatus, setProvisioningStatus] = React.useState<ProvisioningStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = React.useState(true);
  const [theme, setTheme] = React.useState<string>("default");
  const [context, setContext] = React.useState<teamsJs.app.Context | null>(null);
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = React.useState(true);
  const [appHostName, setAppHostName] = React.useState<string>("");

  // ✨ MULTI-TENANT: État du tenant
  const [tenantStatus, setTenantStatus] = React.useState<TenantStatus>({
    checking: true,
    exists: false,
  });

  // 🔐 Configuration validity state (Service Principal + Permissions)
  const [hasValidConfiguration, setHasValidConfiguration] = React.useState<boolean>(false);
  const [configUpdateCount, setConfigUpdateCount] = React.useState<number>(0);

  // 🎨 Helper function to determine if theme is dark
  const isDarkTheme = React.useMemo(() => {
    return theme === "dark" || theme === "contrast";
  }, [theme]);

  // 🔄 Stable callback for configuration updates with detailed logging
  const handleValidConfigurationChange = React.useCallback((isValid: boolean) => {
    console.log("[App] 🔐 handleValidConfigurationChange called with:", isValid, {
      previousValue: hasValidConfiguration,
      updateCount: configUpdateCount + 1
    });

    if (isValid !== hasValidConfiguration) {
      console.log("[App] ✅ Configuration validity CHANGED:", hasValidConfiguration, "→", isValid);
      setHasValidConfiguration(isValid);
      setConfigUpdateCount(prev => prev + 1);
    } else {
      console.log("[App] 🧘 Configuration validity unchanged, already:", isValid);
    }
  }, [hasValidConfiguration, configUpdateCount]);

   const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

   // 🌐 Wrapper component for AdminPage with i18n support
   const AdminPageWrapper: React.FC<{
     checkingStatus: boolean;
     provisioningStatus: ProvisioningStatus | null;
     isAdmin: boolean;
     isCheckingAdmin: boolean;
     theme: string;
     teamId: string | null;
     userId: string;
     onNavigateToSetup: () => void;
   }> = ({ checkingStatus, provisioningStatus, isAdmin, isCheckingAdmin, theme, teamId, userId, onNavigateToSetup }) => {
     const { t } = useTranslation('common');
     
     if (!checkingStatus && provisioningStatus && provisioningStatus.isConfigured) {
       if (isAdmin) {
         return <AdminPage theme={theme} teamId={teamId} currentUserId={userId} />;
       } else if (!isCheckingAdmin) {
         return (
           <div className="error-message">
             <h3>{t('errors.accessDenied', 'Accès refusé')}</h3>
             <p>{t('errors.noPermissions', 'Vous n\'avez pas les permissions nécessaires pour accéder à cette page.')}</p>
           </div>
         );
       }
     }
     
     return (
       <div className="app-unconfigured-page">
         <h2>⚙️ {t('navigation:tabs.admin', 'Administration')}</h2>
         <p>{t('messages.adminNotConfigured', 'L\'administration n\'est accessible qu\'après la configuration initiale de l\'application.')}</p>
         <button
           className="btn-primary"
           onClick={onNavigateToSetup}
         >
           {t('buttons.configure', 'Configurer l\'application')}
         </button>
       </div>
     );
   };

   // Initialize Teams and check provisioning status
  React.useEffect(() => {
    (async () => {
      try {
        await teamsJs.app.initialize();
        setIsInTeams(true);
        const ctx = await teamsJs.app.getContext();
        setContext(ctx);

        // Debug: afficher tout le contexte
        console.log("Teams Context:", ctx);
        console.log("User info:", ctx?.user);

        // Créer une info de debug complète
        const debugData = {
          displayName: ctx?.user?.displayName,
          userPrincipalName: ctx?.user?.userPrincipalName,
          id: ctx?.user?.id,
          loginHint: ctx?.user?.loginHint,
          allUserProps: Object.keys(ctx?.user || {})
        };
        setDebugInfo(JSON.stringify(debugData, null, 2));

        if (ctx?.app?.host?.name) {
          setAppHostName(ctx.app.host.name);
        }

        // Essayer différentes propriétés pour le nom d'utilisateur
        // Vérifier que displayName n'est pas juste une chaîne vide
        let userDisplayName = (ctx?.user?.displayName && ctx.user.displayName.trim()) ||
                               ctx?.user?.userPrincipalName ||
                               ctx?.user?.loginHint ||
                               "Utilisateur inconnu";

        // Si c'est un email, extraire la partie avant le @
        if (userDisplayName.includes('@')) {
          userDisplayName = userDisplayName.split('@')[0];
        }

        const currentUserId = ctx?.user?.id || "";
        const currentUserEmail = ctx?.user?.userPrincipalName || ctx?.user?.loginHint || "";

        setUserName(userDisplayName);
        setUserId(currentUserId);
        setUserEmail(currentUserEmail);

        // Récupérer l'ID de l'équipe Teams où l'app est installée
        console.log(">>>>>>>>> ATTEMPTING TO GET TEAM ID. THIS LOG CONFIRMS THE SCRIPT IS UPDATED. <<<<<<<<<");
        const hostTeamId = ctx?.team?.groupId || null;
        setTeamId(hostTeamId);
        console.log("User display name:", userDisplayName);
        console.log("User ID:", currentUserId);
        console.log("User email:", currentUserEmail);
        console.log("Host team ID:", hostTeamId);

        // Detect and set theme
        const teamsTheme = ctx?.app?.theme || "default";
        setTheme(teamsTheme);
        console.log("Teams theme:", teamsTheme);

        // Register theme change handler
        teamsJs.app.registerOnThemeChangeHandler((newTheme: string) => {
          console.log("Theme changed to:", newTheme);
          setTheme(newTheme);
        });

        // Détecter l'onglet actif depuis le contexte Teams
        const entityId = ctx?.page?.id;
        console.log("Teams page entityId:", entityId);

        if (entityId === "requests") {
          setCurrentTab("requests");
        } else if (entityId === "index0") {
          setCurrentTab("home");
        }

        // ✨ MULTI-TENANT: Check tenant status FIRST
        await checkTenantStatus(ctx?.user?.tenant?.id);

        // Check provisioning status
        await checkProvisioningStatus();

        // Check if user is administrator - PASSER LES VALEURS DIRECTEMENT
        await checkUserAdminStatus(currentUserId, currentUserEmail, userDisplayName);

        // 🔐 Vérifier la configuration initiale (Service Principal + Permissions)
        await checkInitialConfigurationStatus();

        // 📊 TELEMETRY: Identify user via telemetry API
        if (telemetryService.isInitialized()) {
          await telemetryService.identify({
            userId: currentUserId,
            email: currentUserEmail,
            name: userDisplayName,
            tenantId: ctx?.user?.tenant?.id,
            teamId: hostTeamId,
            isInTeams: true,
          });
          await telemetryService.capture('teams_app_loaded', currentUserId, {
            properties: {
              tenantId: ctx?.user?.tenant?.id,
              teamId: hostTeamId,
              isInTeams: true,
              $current_url: window.location.pathname,
            }
          });
        }
      } catch (error) {
        // Pas dans Teams - utiliser des données de test pour le développement local
        console.log("Not running in Teams, using mock data for development", error);
        setIsInTeams(false);
        setAppHostName("LOCAL DEVELOPMENT MODE");
        const testUserId = "test-user-id";
        const testUserEmail = "test@example.com";
        const testUserName = "Utilisateur Test (Mode Local)";

        setUserName(testUserName);
        setUserId(testUserId);
        setUserEmail(testUserEmail);
        setTheme("default");

        // ✨ MULTI-TENANT: Check tenant status FIRST (with mock data)
        await checkTenantStatus("test-tenant-id");

        // Check provisioning status
        await checkProvisioningStatus();

        // Check if user is administrator - PASSER LES VALEURS DIRECTEMENT
        await checkUserAdminStatus(testUserId, testUserEmail, testUserName);

        // 📊 TELEMETRY: Identify user via telemetry API (development mode)
        if (telemetryService.isInitialized()) {
          await telemetryService.identify({
            userId: testUserId,
            email: testUserEmail,
            name: testUserName,
            isInTeams: false,
            isDevelopment: true,
          });
          await telemetryService.capture('teams_app_loaded', testUserId, {
            properties: {
              isInTeams: false,
              isDevelopment: true,
              $current_url: window.location.pathname,
            }
          });
        }
      }
    })();
  }, []);

  // 📊 TELEMETRY: Track page navigation
  React.useEffect(() => {
    if (telemetryService.isInitialized() && userId) {
      const pageName = `teams_app_${currentTab}_viewed`;
      telemetryService.trackPageView(pageName, userId, {
        tab: currentTab,
        userId: userId,
        isAdmin: isAdmin,
        isInTeams: isInTeams,
        virtualUrl: `${window.location.origin}/tabs/${currentTab}/`,
        appHostName: appHostName,
      });
      console.log('[Telemetry] Tracked page view:', pageName, { appHostName });
    }
  }, [currentTab, userId, isAdmin, isInTeams, appHostName]);

  /**
   * ✨ MULTI-TENANT: Check tenant registration status
   * This runs BEFORE provisioning check to ensure tenant exists
   */
  const checkTenantStatus = async (azureTenantId?: string) => {
    try {
      setTenantStatus({ checking: true, exists: false });
      console.log("[App] Checking tenant status...", { azureTenantId });

      if (!azureTenantId) {
        console.warn("[App] No Azure Tenant ID available");
        setTenantStatus({
          checking: false,
          exists: false,
          error: "Azure Tenant ID non disponible",
        });
        return;
      }

      const response = await fetch(
        buildApiUrlWithParams('/tenants/check', { azureTenantId })
      );
      const data = await response.json();

      console.log("[App] Tenant check response:", data);

      if (data.success) {
        setTenantStatus({
          checking: false,
          exists: data.exists,
          tenantId: data.tenant?.tenantId,
          azureTenantId,
        });

        if (!data.exists) {
          console.log("[App] Tenant not registered - redirecting to setup");
          setCurrentTab("setup");
        } else {
          console.log("[App] Tenant exists:", data.tenant?.tenantId);
        }
      } else {
        console.error("[App] Tenant check failed:", data.error);
        setTenantStatus({
          checking: false,
          exists: false,
          error: data.error || "Erreur lors de la vérification du tenant",
          azureTenantId,
        });
      }
    } catch (error: any) {
      console.error("[App] Error checking tenant status:", error);
      setTenantStatus({
        checking: false,
        exists: false,
        error: error.message || "Erreur de connexion au serveur",
      });
    }
  };

  const checkProvisioningStatus = async () => {
    try {
      setCheckingStatus(true);
      console.log("[App] Checking provisioning status...");

      // ✨ MULTI-TENANT: Use apiService to include tenant headers
      const data = await apiService.getProvisioningStatus();
      console.log("[App] Provisioning status data:", data);

      if (data.success) {
        setProvisioningStatus(data.data);
        console.log("[App] Provisioning status set:", {
          isConfigured: data.data.isConfigured,
          listsExist: data.data.listsExist,
          errors: data.data.errors
        });

        // Si non configuré, rediriger automatiquement vers la page de configuration
        if (!data.data.isConfigured) {
          console.log("[App] Not configured - redirecting to setup page");
          setCurrentTab("setup");
        } else {
          console.log("[App] Configuration OK - all lists exist");
        }
      } else {
        console.error("[App] Provisioning status check failed:", data.error);
      }
    } catch (error) {
      console.error("[App] Error checking provisioning status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleConfigured = async () => {
    // ✨ MULTI-TENANT: Rafraîchir le statut tenant ET provisioning
    if (context?.user?.tenant?.id) {
      await checkTenantStatus(context.user.tenant.id);
    }
    await checkProvisioningStatus();
    setCurrentTab("home");
  };

  const handleNavigateToAdmin = async () => {
    // Recharger le statut de provisioning ET administrateur avant de naviguer
    console.log("Reloading provisioning and admin status before navigation...");
    await checkProvisioningStatus();
    await checkUserAdminStatus(userId, userEmail, userName);

    // Naviguer vers la page d'administration
    setCurrentTab("admin");
  };

  const checkUserAdminStatus = async (currentUserId: string, currentUserEmail: string, currentUserName: string) => {
    try {
      setIsCheckingAdmin(true);

      console.log("Checking admin status for user:", { currentUserId, currentUserEmail, currentUserName });

      // Si l'ID utilisateur est vide, on ne peut pas vérifier
      if (!currentUserId) {
        console.warn("User ID is empty, cannot check admin status");
        setIsAdmin(false);
        return;
      }

      // 🧪 DEVELOPMENT MODE: Allow forcing admin status via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const forceAdminParam = urlParams.get('forceAdmin');
      if (forceAdminParam === 'true' && !isInTeams) {
        console.log("[DEV MODE] 🔓 Forcing admin status via URL parameter");
        setIsAdmin(true);
        return;
      }

      // Vérifier si l'utilisateur est administrateur
      const adminResponse = await apiService.checkIsAdministrator(currentUserId);

      console.log("Admin check response:", adminResponse);

      if (adminResponse.success && adminResponse.data) {
        setIsAdmin(adminResponse.data.isAdministrator);

        console.log("Is user admin?", adminResponse.data.isAdministrator);

        // Si ce n'est pas un admin, vérifier s'il n'y a aucun administrateur (pour initialiser le premier)
        if (!adminResponse.data.isAdministrator) {
          const allAdminsResponse = await apiService.getAllAdministrators();

          console.log("All admins response:", allAdminsResponse);

          if (allAdminsResponse.success && allAdminsResponse.data && allAdminsResponse.data.length === 0) {
            // Aucun administrateur n'existe, créer le premier administrateur avec l'utilisateur actuel
            console.log("Aucun administrateur trouvé, création du premier administrateur...");

            const firstAdmin = {
              id: currentUserId,
              email: currentUserEmail,
              displayName: currentUserName,
              addedDate: new Date().toISOString()
            };

            const createAdminResponse = await apiService.addAdministrator(firstAdmin);

            console.log("Create admin response:", createAdminResponse);

            if (createAdminResponse.success) {
              console.log("Premier administrateur créé avec succès");
              setIsAdmin(true);
            } else {
              console.error("Erreur lors de la création du premier administrateur:", createAdminResponse.error);
            }
          }
        }
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du statut administrateur:", error);
      // En cas d'erreur, on considérer que l'utilisateur n'est pas admin par défaut
      setIsAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  /**
   * 🔐 Vérifie le statut de configuration (Service Principal + Permissions) au démarrage
   * Cette fonction est appelée dans le useEffect initial pour déterminer hasValidConfiguration
   */
  const checkInitialConfigurationStatus = async () => {
    try {
      console.log("[App] 🔐 Checking initial configuration status...");

      // Vérifier si on est dans Teams (sinon, c'est du développement local)
      const isTeamsEnvironment = isInTeams;

      if (!isTeamsEnvironment) {
        console.log("[App] 🧪 Development mode - checking local configuration");
        // En mode dev, considérer que la configuration est valide par défaut
        handleValidConfigurationChange(true);
        return;
      }

      // Vérifier le Service Principal en essayant d'obtenir un token
      let servicePrincipalMissing = false;
      let permissionsGranted = false;

      try {
        // Essayer d'obtenir un token utilisateur pour vérifier le Service Principal
        const userToken = await getTeamsUserToken();
        if (userToken) {
          console.log("[App] ✅ Service Principal exists - user token obtained");
          servicePrincipalMissing = false;
        }
      } catch (tokenError: any) {
        console.warn("[App] ⚠️ Could not get user token:", tokenError.message);

        // Détecter si le Service Principal est manquant
        if (tokenError.message.includes("AADSTS500011") ||
            tokenError.message.includes("resource principal") ||
            tokenError.message.includes("was not found in the tenant")) {
          console.error("[App] ❌ Service Principal missing in tenant");
          servicePrincipalMissing = true;
        }
      }

      // Si le Service Principal existe, vérifier les permissions via l'endpoint /setup/permissions-status
      if (!servicePrincipalMissing) {
        try {
          console.log("[App] 🔍 Checking permissions status...");
          const permissionsResponse = await fetch(buildApiUrlWithParams('/setup/permissions-status'), {
            headers: {
              "Content-Type": "application/json",
              "X-Azure-Tenant-Id": context?.user?.tenant?.id || ""
            }
          });

          const permissionsData = await permissionsResponse.json();

          if (permissionsData.success) {
            permissionsGranted = permissionsData.data.summary.allRequiredGranted;
            console.log("[App] 📊 Permissions status:", {
              allRequiredGranted: permissionsGranted,
              totalRequired: permissionsData.data.summary.totalRequired,
              requiredGranted: permissionsData.data.summary.requiredGranted
            });
          } else {
            console.error("[App] ❌ Failed to get permissions status:", permissionsData.error);
          }
        } catch (permissionsError) {
          console.error("[App] ❌ Error checking permissions:", permissionsError);
        }
      }

      // Déterminer le statut final de la configuration
      const hasValidConfig = !servicePrincipalMissing && permissionsGranted;
      console.log("[App] 🔐 Initial configuration status determined:", {
        servicePrincipalMissing,
        permissionsGranted,
        hasValidConfig
      });

      // Mettre à jour l'état
      handleValidConfigurationChange(hasValidConfig);

    } catch (error) {
      console.error("[App] ❌ Error checking initial configuration:", error);
      // En cas d'erreur, considérer que la configuration n'est pas valide
      handleValidConfigurationChange(false);
    }
  };

  // ✨ MULTI-TENANT: Redirect to setup if tenant error (for all users)
  React.useEffect(() => {
    if (!tenantStatus.checking && tenantStatus.error && !isCheckingAdmin) {
      console.log("[App] Tenant error detected", {
        error: tenantStatus.error,
        isAdmin,
      });

      // Auto-redirect to setup for all users when tenant is not found
      console.log("[App] Tenant not found - redirecting to setup page");
      setCurrentTab("setup");
    }
  }, [tenantStatus.checking, tenantStatus.error, isCheckingAdmin]);

  // Store appHostName in window for access from other components
  React.useEffect(() => {
    (window as any).__appHostName = appHostName;
  }, [appHostName]);

  return (
    <I18nextProvider i18n={i18n}>
      <FluentProvider theme={isDarkTheme ? webDarkTheme : webLightTheme}>
        <div className={`App theme-${theme}`}>
          <h1>👋 Welcome [{userName && `${userName}`}]</h1>

      {!isInTeams && (
        <div className="warning-card">
          ⚠️ Mode développement local - Les données utilisateur sont simulées
        </div>
      )}

      {/* ✨ MULTI-TENANT: Tenant Error Banner (for non-admin users) */}
      {!tenantStatus.checking && tenantStatus.error && !isAdmin && !isCheckingAdmin && currentTab !== "setup" && (
        <div className="error-card app-tenant-error">
          <div className="icon-large">🚫</div>
          <strong className="section-header">
            Application non configurée
          </strong>
          <p className="app-tenant-error-message">
            L'application n'est pas encore configurée pour votre organisation.
            <br />
            Veuillez contacter le support technique ou un administrateur.
          </p>
          <div className="info-card app-tenant-error-details">
            <strong>Erreur technique :</strong> {tenantStatus.error}
          </div>
        </div>
      )}

      {/* Configuration Warning Banner */}
      {!checkingStatus && provisioningStatus && !provisioningStatus.isConfigured && currentTab !== "setup" && (
        <div className="warning-card app-config-warning">
          <div>
            <strong>⚠️ Configuration Requise</strong>
            <p className="app-config-warning-text">
              Les listes SharePoint ne sont pas configurées. L'application ne fonctionnera pas correctement.
            </p>
          </div>
          <button
            className="btn-primary"
            onClick={() => setCurrentTab("setup")}
          >
            Configurer
          </button>
        </div>
      )}

      {/* Navigation - Only show if user is admin or admin check is complete */}
      {isAdmin && (
        <Navigation
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          showSetupWarning={!checkingStatus && provisioningStatus ? !provisioningStatus.isConfigured : false}
          theme={theme}
          isConfigured={!checkingStatus && provisioningStatus ? provisioningStatus.isConfigured : false}
          isAdmin={isAdmin}
          isLoading={isCheckingAdmin}
          hasValidConfiguration={hasValidConfiguration}
        />
      )}

      {/* Content based on current tab */}
      {currentTab === "home" && (
        <>
          <div className="app-main-content">
            {/* ✨ MULTI-TENANT: Block form if tenant error */}
            {!tenantStatus.checking && tenantStatus.error && !isAdmin && !isCheckingAdmin ? (
              <div className="app-form-disabled">
                <div className="app-form-disabled-icon">🔒</div>
                <h3 className="app-form-disabled-title">Formulaire désactivé</h3>
                <p className="app-form-disabled-text">
                  L'application doit être configurée avant de pouvoir soumettre des demandes.
                </p>
              </div>
            ) : (
              <TeamRequestForm
                currentUserName={userName || "Utilisateur"}
                currentUserId={userId}
                currentUserEmail={userEmail}
                teamId={teamId}
                tenantError={tenantStatus.error}
              />
            )}
          </div>
        </>
      )}

      {currentTab === "setup" && (
        <SetupPage
          onConfigured={handleConfigured}
          onNavigateToAdmin={handleNavigateToAdmin}
          onValidConfigurationChange={handleValidConfigurationChange}
          theme={theme}
        />
      )}

       {currentTab === "admin" && (
         <AdminPageWrapper
           checkingStatus={checkingStatus}
           provisioningStatus={provisioningStatus}
           isAdmin={isAdmin}
           isCheckingAdmin={isCheckingAdmin}
           theme={theme}
           teamId={teamId}
           userId={userId}
           onNavigateToSetup={() => setCurrentTab("setup")}
         />
       )}

      {currentTab === "approvals" && (
        !checkingStatus && provisioningStatus && provisioningStatus.isConfigured ? (
          <RequestsPage theme={theme} />
        ) : (
          <div className="app-unconfigured-page">
            <h2>✅ Approbations</h2>
            <p>Les approbations ne sont accessibles qu'après la configuration initiale de l'application.</p>
            <button
              className="btn-primary"
              onClick={() => setCurrentTab("setup")}
            >
              Configurer l'application
            </button>
          </div>
        )
      )}

      {currentTab === "requests" && (
        !checkingStatus && provisioningStatus && provisioningStatus.isConfigured ? (
          <RequestsPage theme={theme} />
        ) : (
          <div className="app-unconfigured-page">
            <h2>📋 Demandes</h2>
            <p>Les demandes ne sont accessibles qu'après la configuration initiale de l'application.</p>
            <button
              className="btn-primary"
              onClick={() => setCurrentTab("setup")}
            >
              Configurer l'application
            </button>
          </div>
        )
      )}

      {currentTab === "debug" && (
        !checkingStatus && provisioningStatus && provisioningStatus.isConfigured ? (
          isAdmin ? (
            <DebugApprovalsPage />
          ) : (
            !isCheckingAdmin && (
              <div className="error-message">
                <h3>Accès refusé</h3>
                <p>Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
              </div>
            )
          )
        ) : (
          <div className="app-unconfigured-page">
            <h2>🔍 Debug</h2>
            <p>Le debug n'est accessible qu'après la configuration initiale de l'application.</p>
            <button
              className="btn-primary"
              onClick={() => setCurrentTab("setup")}
            >
              Configurer l'application
            </button>
          </div>
        )
      )}
        </div>
      </FluentProvider>
    </I18nextProvider>
  );
}
